import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogTitle, DialogContent, IconButton, Slider, Tooltip, Typography } from "@material-ui/core";
import { Close, GetApp } from "@material-ui/icons";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
	imageWrapper: {
		position: "relative",
		display: "inline-flex",
		alignItems: "flex-end",
		gap: theme.spacing(1),
		maxWidth: "100%",
	},
	messageMedia: {
		objectFit: "cover",
		width: 280,
		maxWidth: "100%",
		height: "auto",
		borderRadius: theme.spacing(1), // ✅ Cantos arredondados apenas na imagem
		padding: 0, // ✅ Sem padding - a bolha já tem
		backgroundColor: "transparent", // ✅ Transparente - sem card duplicado
		boxShadow: "none", // ✅ Sem sombra - a bolha já tem
		display: "block",
		margin: "0 auto",
		cursor: "zoom-in",
	},
	actionBar: {
		display: "flex",
		alignSelf: "center",
	},
	downloadButton: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
		"&:hover": {
			backgroundColor: theme.palette.primary.dark,
		},
	},
	dialogPaper: {
		borderRadius: theme.spacing(1.5),
		overflow: "hidden",
		maxHeight: "88vh",
		display: "flex",
		flexDirection: "column",
	},
	dialogTitle: {
		position: "relative",
		padding: theme.spacing(1.5, 2.5),
		background: "linear-gradient(135deg, #1d4ed8 0%, #7c3aed 100%)",
		color: "#fff",
	},
	titleText: {
		fontWeight: 700,
		fontSize: "1rem",
		color: "#fff",
		paddingRight: theme.spacing(10),
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	titleActions: {
		position: "absolute",
		top: 8,
		right: 10,
		display: "flex",
		gap: theme.spacing(0.5),
	},
	titleActionButton: {
		color: "#fff",
	},
	metaLine: {
		display: "block",
		marginBottom: 10,
		fontWeight: 600,
	},
	dialogContent: {
		position: "relative",
		outline: "none",
		maxWidth: "90vw",
		overflow: "hidden !important",
		display: "flex",
		flexDirection: "column",
		flex: 1,
		minHeight: 0,
	},
	previewModal: {
		width: "100%",
		height: "calc(88vh - 140px)",
		maxHeight: "calc(88vh - 140px)",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		position: "relative",
		overflow: "hidden",
	},
	viewerStage: {
		position: "relative",
		width: "100%",
		height: "100%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		overflow: "hidden",
		cursor: "grab",
	},
	viewerDragging: {
		cursor: "grabbing",
	},
	dialogImage: {
		maxWidth: "100%",
		maxHeight: "100%",
		width: "auto",
		height: "auto",
		objectFit: "contain",
		borderRadius: theme.spacing(1),
		userSelect: "none",
		pointerEvents: "none",
		transformOrigin: "center center",
	},
	imageCanvas: {
		position: "relative",
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		width: "100%",
		height: "100%",
		maxWidth: "100%",
		maxHeight: "100%",
		overflow: "hidden",
	},
	dialogIconButton: {
		backgroundColor: "rgba(0, 0, 0, 0.55)",
		color: "#fff",
		"&:hover": {
			backgroundColor: "rgba(0, 0, 0, 0.75)",
		},
	},
	zoomBar: {
		position: "absolute",
		bottom: 10,
		left: "50%",
		transform: "translateX(-50%)",
		zIndex: 30,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 10,
		width: 260,
		maxWidth: "80vw",
		padding: "8px 14px",
		borderRadius: 999,
		background: "rgba(15, 23, 42, 0.72)",
		backdropFilter: "blur(8px)",
		WebkitBackdropFilter: "blur(8px)",
		boxShadow: "0 4px 18px rgba(0, 0, 0, 0.28), 0 1px 2px rgba(255,255,255,0.06) inset",
		pointerEvents: "auto",
		"@media (max-width: 768px)": {
			bottom: 10,
			transform: "translateX(-50%) scale(0.95)",
		},
	},
	zoomSlider: {
		color: "#fff",
	},
}));

const ModalImageCors = ({
	imageUrl,
	filename = "imagem",
	createdAt,
	fromMe = false,
	open: controlledOpen,
	onClose,
	hideInlineTrigger = false,
}) => {
	const classes = useStyles();
	const [fetching, setFetching] = useState(true);
	const [blobUrl, setBlobUrl] = useState("");
	const [internalOpen, setInternalOpen] = useState(false);
	const [zoom, setZoom] = useState(1);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [dragging, setDragging] = useState(false);
	const dragStartRef = useRef({ x: 0, y: 0 });

	useEffect(() => {
		if (!imageUrl) return;
		const fetchImage = async () => {
			try {
				// ✅ CORRIGIDO: O imageUrl já vem do backend com a URL completa (incluindo BACKEND_URL)
				// O modelo Message constrói: ${BACKEND_URL}/public/company${companyId}/flow/${fileName}
				// Então precisamos extrair apenas o caminho relativo para usar com api.get()
				let normalizedUrl = imageUrl;
				
				if (normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://')) {
					// Se já é uma URL completa, extrair apenas o caminho relativo
					try {
						const urlObj = new URL(normalizedUrl);
						normalizedUrl = urlObj.pathname; // Pega apenas o caminho (ex: /public/company1/flow/logo-swl-plus.png)
					} catch (e) {
						// Se falhar ao parsear, usar como está
						console.warn("[ModalImageCors] Erro ao parsear URL:", e);
					}
				} else if (!normalizedUrl.startsWith('/')) {
					// Se não começa com /, adicionar
					normalizedUrl = `/${normalizedUrl}`;
				}
				
				// Agora normalizedUrl está no formato: /public/company1/flow/logo-swl-plus.png
				// O api.get() vai fazer: baseURL + normalizedUrl = BACKEND_URL + /public/company1/flow/logo-swl-plus.png
				
				const { data, headers } = await api.get(normalizedUrl, {
					responseType: "blob",
				});
				const url = window.URL.createObjectURL(
					new Blob([data], { type: headers["content-type"] })
				);
				setBlobUrl(url);
				setFetching(false);
			} catch (error) {
				console.error("[ModalImageCors] Erro ao carregar imagem:", error);
				console.error("[ModalImageCors] URL tentada:", imageUrl);
				setFetching(false);
			}
		};
		fetchImage();
	}, [imageUrl]);

	const isControlled = typeof controlledOpen === "boolean";
	const open = isControlled ? controlledOpen : internalOpen;
	const handleOpen = () => {
		if (!isControlled) {
			setInternalOpen(true);
		}
	};
	const handleClose = () => {
		if (!isControlled) {
			setInternalOpen(false);
		}
		if (typeof onClose === "function") {
			onClose();
		}
	};

	useEffect(() => {
		if (!open) return;
		setZoom(1);
		setPosition({ x: 0, y: 0 });
		setDragging(false);
	}, [open]);

	const resolvedUrl = fetching ? imageUrl : blobUrl;

	const handleDownload = (event) => {
		if (event?.stopPropagation) {
			event.stopPropagation();
		}

		try {
			const link = document.createElement("a");
			link.href = resolvedUrl;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (error) {
			console.error("[ModalImageCors] Erro no download da imagem:", error);
			window.open(resolvedUrl, "_blank", "noopener,noreferrer");
		}
	};

	const handleMouseDown = (event) => {
		if (zoom <= 1) return;
		setDragging(true);
		dragStartRef.current = {
			x: event.clientX - position.x,
			y: event.clientY - position.y,
		};
	};

	const handleMouseMove = (event) => {
		if (!dragging || zoom <= 1) return;
		setPosition({
			x: event.clientX - dragStartRef.current.x,
			y: event.clientY - dragStartRef.current.y,
		});
	};

	const stopDragging = () => {
		if (dragging) setDragging(false);
	};

	const handleZoomChange = (_event, value) => {
		const nextZoom = Array.isArray(value) ? value[0] : value;
		setZoom(nextZoom);
		if (nextZoom <= 1) {
			setPosition({ x: 0, y: 0 });
		}
	};

	return (
		<>
			{!hideInlineTrigger && (
				<div className={`${classes.imageWrapper} ticket-image-wrapper`}>
					<img
						src={resolvedUrl}
						alt={filename}
						className={classes.messageMedia}
						onClick={handleOpen}
					/>
					<div className={classes.actionBar}>
						<Tooltip title={i18n.t("modalImageCors.downloadImage")}>
							<IconButton
								className={classes.downloadButton}
								onClick={handleDownload}
								size="small"
							>
								<GetApp />
							</IconButton>
						</Tooltip>
					</div>
				</div>
			)}
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="md"
				fullWidth
				classes={{ paper: classes.dialogPaper }}
			>
				<DialogTitle className={classes.dialogTitle}>
					<Typography className={classes.titleText}>{filename || i18n.t("modalImageCors.image")}</Typography>
					<div className={classes.titleActions}>
						<Tooltip title={i18n.t("modalImageCors.downloadImage")}>
							<IconButton
								className={classes.titleActionButton}
								onClick={handleDownload}
								size="small"
							>
								<GetApp />
							</IconButton>
						</Tooltip>
						<Tooltip title={i18n.t("common.buttons.close")}>
							<IconButton
								className={classes.titleActionButton}
								onClick={handleClose}
								size="small"
							>
								<Close />
							</IconButton>
						</Tooltip>
					</div>
				</DialogTitle>
				<DialogContent className={`${classes.dialogContent} image-preview-modal-content`}>
					<Typography variant="caption" className={classes.metaLine}>
						{i18n.t(fromMe ? "modalImageCors.sentAt" : "modalImageCors.receivedAt", { date: createdAt ? new Date(createdAt).toLocaleString("pt-BR") : "-" })}
					</Typography>
					<div
						className={`${classes.previewModal} image-preview-container message-image-container`}
					>
						<div
							className={`${classes.viewerStage} ${dragging ? classes.viewerDragging : ""}`}
							onMouseDown={handleMouseDown}
							onMouseMove={handleMouseMove}
							onMouseUp={stopDragging}
							onMouseLeave={stopDragging}
						>
							<div className={`${classes.imageCanvas} image-preview-container message-image-container`}>
								<img
									src={resolvedUrl}
									alt={filename}
									className={classes.dialogImage}
									draggable={false}
									style={{
										transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
									}}
								/>
								<div className={`${classes.zoomBar} ticket-image-zoom-controls image-zoom-controls zoom-controls`}>
									<Slider
										className={classes.zoomSlider}
										value={zoom}
										onChange={handleZoomChange}
										min={1}
										max={4}
										step={0.1}
										aria-label={i18n.t("modalImageCors.imageZoom")}
									/>
								</div>
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default ModalImageCors;
